import { useMemo, useState } from "react";
import { Dialog, Grid, DialogContent, Typography, styled } from "@mui/material";

import { ModalTitle, ModalButton, ModalMessage } from "src/components/DialogModal/DialogModalComponents";
import { BuildStatus, defaultDateRange } from "../BuildStatus/BuildStatus";
import { IFlightTemplate } from "@vmosolutions/oc-interfaces";
import { DateTimeField } from "src/components/FormikComponents/DateTimeField";
import moment from "moment-timezone";

export interface IDialogMessage {
  type: null;
  title?: string;
  mainMessage?: string;
  yesButton?: string;
  noButton?: string;
}
interface IBuildFolderFormDialogProps {
  open: boolean;
  handleClose: (value: boolean) => void;
  message: IDialogMessage;
  handleYesClick?: () => void;
  //handleNoClick?: { value: () => void };
  className?: string;
  buildStatusPayload?: IFlightTemplate[];
}

const OCBuildFolderFormDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    width: "100%",
    height: "90%",
    marginLeft: 300,
    // Customize styling for the Build Status window when it's used within this dialog window.
    "& .build_folder_status": {
      width: "calc(100% - 50px)",
      height: "calc(100% - 110px)",
    },
    "& .inline_build_status": {
      width: "100%",
      "& .build_folder_status": {
        minHeight: 100,
        width: "100%",
        height: "auto",
        position: "relative",
        overflowY: "hidden",
        "& .table_container": { overflowY: "hidden" },
      },
    },
  },
}));

// Default start and until times.
const [defaultStart, defaultEnd] = defaultDateRange();

export const BuildFolderFormDialog = (props: IBuildFolderFormDialogProps): JSX.Element => {
  const { open, message, handleClose, buildStatusPayload } = props;

  const [ruleStart, setRuleStart] = useState<number>(defaultStart);
  const [ruleUntil, setRuleUntil] = useState<number>(defaultEnd);

  // Filter out all folders and only retain leaf nodes.
  const actualTemplates = useMemo(
    () => buildStatusPayload.filter((treeItem) => treeItem.isLeafNode),
    [buildStatusPayload]
  );

  if (open) {
    return (
      <div onClick={(event) => event.stopPropagation()}>
        <OCBuildFolderFormDialog open={open} onClose={() => handleClose(false)}>
          <DialogContent>
            <Grid container>
              <ModalTitle title={message.title} />
              <ModalButton label={message.noButton} handler={() => handleClose(false)} />
            </Grid>

            <Grid container className="form_body">
              <ModalMessage message={message.mainMessage} />
            </Grid>

            <Grid container style={{ width: "100%", marginBottom: 15 }}>
              <Grid item>
                <DateTimeField
                  label="Start"
                  className="start_date"
                  name="start"
                  value={ruleStart?.valueOf()}
                  onChange={(date) => setRuleStart(moment.utc(date).startOf("day")?.valueOf())}
                  isEpoch={true}
                  hideTimeControl={true}
                  maxDate={ruleUntil?.valueOf()}
                />
              </Grid>
              <Grid item>
                <DateTimeField
                  label="Until"
                  className="until_date"
                  name="until"
                  value={ruleUntil?.valueOf()}
                  onChange={(date) => setRuleUntil(moment.utc(date).endOf("day")?.valueOf())}
                  isEpoch={true}
                  meta={{
                    error: ruleStart > ruleUntil ? "Until date must be after start." : null,
                    touched: ruleStart > ruleUntil ? true : false,
                  }}
                  hideTimeControl={true}
                  minDate={ruleStart?.valueOf()}
                />
              </Grid>
            </Grid>

            {actualTemplates.map((template) => {
              return (
                <Grid container key={template?._id} style={{ marginBottom: 15 }}>
                  <Typography variant="caption">{template?.fileName}</Typography>
                  <Grid item className="inline_build_status">
                    <BuildStatus
                      mosaicId={null}
                      className="build_folder_status"
                      eventType={{ value: "RecurrentFlight", label: "Recurring Flights" }}
                      payload={{ templateFile: template }}
                      parentRuleStart={ruleStart}
                      parentRuleUntil={ruleUntil}
                    />
                  </Grid>
                </Grid>
              );
            })}
          </DialogContent>
        </OCBuildFolderFormDialog>
      </div>
    );
  } else return null;
};
